// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { createTheme } from '@material-ui/core/styles';
import { Overrides, ComponentNameToClassKey } from '@material-ui/core/styles/overrides';
import { ComponentsPropsList } from '@material-ui/core/styles/props';
import { StyleRules } from '@material-ui/core/styles/withStyles';
import { Colors } from 'react-scale/components/legacy-css/global_css';
import color from 'color';

/** BUG: MUI-defined Overrides ignores props interfaces for dynamic styles */
type OverridesWithProps = {
  [Name in keyof ComponentNameToClassKey]?: Partial<
    // @ts-ignore using Name to index into ComponentsPropsList
    StyleRules<ComponentNameToClassKey[Name], ComponentsPropsList[Name]>
  >;
};

const scaleAITheme = {
  typography: {
    // sets global font-size to 16px
    fontSize: 16,
    htmlFontSize: 16,
    fontFamily: 'var(--font-family)',
    fontFamilyMono: 'var(--font-family-code)',
  },
  palette: {
    common: {
      black: '#000',
      white: '#fff',
      blue: Colors.ScaleBlue,
      orange: '#FD9D52',
      darkOrange: '#F7883C',
      magenta: '#DD45D3',
      darkMagenta: '#C634C3',
      purple: '#943CFF',
      darkPurple: '#763AD4',
      blueberry: '#6A11EA',
      gradient: 'linear-gradient(0deg, #943CFF 0%, #DD45D3 50%, #FD9D52 100%);',
      pink: '#ED72E2',
    },
    background: {
      one: '#F5F4F9',
      two: '#F5F9FA',
    },
    text: {
      blue: '#247EFF',
      gray: '#586371',
    },

    primary: {
      light: color(Colors.ScaleBlue).lighten(0.2).toString(),
      main: Colors.ScaleBlue,
      dark: color(Colors.ScaleBlue).darken(0.2).toString(),
      contrastText: '#fff',
    },
    secondary: {
      light: color(Colors.ScaleBlue).lighten(0.4).toString(),
      main: color(Colors.ScaleBlue).lighten(0.2).toString(),
      dark: Colors.ScaleBlue,
      contrastText: '#fff',
    },
    success: {
      light: color(Colors.RemoGreen).lighten(0.2).toString(),
      main: Colors.RemoGreen,
      dark: color(Colors.RemoGreen).darken(0.2).toString(),
      contrastText: '#fff',
    },
    // same as primary
    info: {
      light: color(Colors.RemoBlue).lighten(0.2).toString(),
      main: Colors.RemoBlue,
      dark: color(Colors.RemoBlue).darken(0.2).toString(),
      contrastText: '#fff',
    },
    warning: {
      light: color(Colors.RemoGold).lighten(0.2).toString(),
      main: Colors.RemoGold,
      dark: color(Colors.RemoGold).darken(0.2).toString(),
      contrastText: '#fff',
    },
    error: {
      light: color(Colors.RemoRed).lighten(0.2).toString(),
      main: Colors.RemoRed,
      dark: color(Colors.RemoRed).darken(0.2).toString(),
      contrastText: '#fff',
    },
  },
  // Due to some components depending on Remotasks-specific theme colors,
  // We want these colors to also be available in customer dashboard;
  colors: {
    primary1: Colors.RemoBlue,
    blue: Colors.ScaleBlue,
    green: Colors.RemoGreen,
    gold: Colors.RemoGold,
    red: Colors.RemoRed,
    white: '#fff',
    gray01: Colors.RemoGray01,
    gray05: Colors.RemoGray05,
    gray10: Colors.RemoGray10,
    gray20: Colors.RemoGray20,
    gray50: Colors.RemoGray50,
    gray75: Colors.RemoGray75,
    black: '#000',
  },
  // WAITING FOR RAPID-NUCLEUS INTEGRATION
  // This code is duplicated in more than one place, please global replace once integration is done
  rapidButton: {
    root: {
      borderRadius: 8,
      padding: '4px 16px !important',
      letterSpacing: 0,

      '&.MuiButton-contained.Mui-disabled': {
        backgroundColor: `#6A5DF9 !important`, // NUCLEUS_COLORS.periwinkle100
        opacity: 0.3,
        color: 'white !important',
      },
    },
    label: {
      textTransform: 'none',
      fontSize: 14,
      fontWeight: 500,
    },
    contained: {
      boxShadow: 'none !important',
      fontWeight: 500,
    },
  },
};

export type ScaleAITheme = typeof scaleAITheme;

// Create a theme instance.
const defaultTheme = createTheme<ScaleAITheme>({
  ...scaleAITheme,
});

/**
 * Overrides in this object are applied across scale.com & remotasks.com
 *
 * To override in only one of these places, add your styles to either
 * `defaultTheme.overrides` for Scale AI marketing & dashboard, or
 * `RemotasksUITheme.overrides` (in components/design-system/theme) for Remotasks & Scale Corp
 */
export const baseOverrides: OverridesWithProps = {
  MuiBadge: {
    badge: {
      border: '1px solid white',
    },
    anchorOriginTopRightRectangle: {
      transform: 'translate(65%, -65%) !important',
    },
  },
  MuiButton: {
    root: {
      fontSize: applyIfNotSmall(defaultTheme.typography.fontSize),
      height: `${defaultTheme.spacing(4)}px !important`,
      padding: `${defaultTheme.spacing(0, 2)} !important`,
      borderRadius: defaultTheme.spacing(1),
      display: 'inline-flex',
      alignItems: 'center',
      transition: 'background 250ms ease-out',
      boxShadow: 'none !important',
    },
    contained: {
      fontWeight: 600,
      letterSpacing: 0.5,
    },
    outlined: {
      fontWeight: 600,
      letterSpacing: 0.5,
    },
    startIcon: {
      '@global svg': {
        fontSize: `${defaultTheme.typography.fontSize}px !important`,
      },
    },
    endIcon: {
      '@global svg': {
        fontSize: `${defaultTheme.typography.fontSize}px !important`,
      },
    },
  },
  MuiChip: {
    root: {
      padding: defaultTheme.spacing(1),
    },
    sizeSmall: {
      padding: defaultTheme.spacing(0, 0.5),
    },
    label: {
      fontSize: applyIfNotSmall(defaultTheme.typography.fontSize),
    },
    avatar: {
      marginRight: '0 !important',
      width: applyIfNotSmall(`${defaultTheme.spacing(1.5)}px !important`),
    },
    icon: {
      marginRight: '0 !important',
      width: applyIfNotSmall('1.2em !important'),
      height: applyIfNotSmall('1.2em'),
    },
    deleteIcon: {
      marginLeft: '0 !important',
      width: applyIfNotSmall(`${defaultTheme.spacing(2)}px !important`),
    },
  },
  MuiDialogActions: {
    root: {
      padding: defaultTheme.spacing(2),
      '@global button + button': {
        marginLeft: defaultTheme.spacing(2),
      },
    },
  },
  MuiIconButton: {
    root: {
      '& span': {
        padding: '0 2px',
      },
      '& svg': {
        transform: 'scale(0.8)',
        transformOrigin: 'center center',
      },
    },
  },
  MuiPaper: {
    rounded: {
      borderRadius: `${defaultTheme.spacing(1)}px !important`,
    },
  },
  MuiSnackbarContent: {
    root: {
      borderRadius: defaultTheme.spacing(1),
    },
  },
  MuiSwitch: {
    root: {
      width: 40,
      height: 20,
      padding: 0,
      overflow: 'visible',
    },
    thumb: {
      width: 20,
      height: 20,
      boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.24), 0px 0px 2px rgba(0, 0, 0, 0.12)',
    },
    track: {
      backgroundColor: '#999999',
      borderRadius: 20 / 2,
      opacity: 1,
    },
    checked: {},
  },
};

defaultTheme.overrides = baseOverrides as Overrides;

export default defaultTheme;

/**
 * Setting styles for default size can also apply them when size="small"
 * We want to avoid this, so it's not necessary to also reset the original style
 */
function applyIfNotSmall<K extends keyof ComponentsPropsList>(
  style: string | number,
  fallback = 'inherit',
) {
  return (props: ComponentsPropsList[K]) =>
    (props as { size: string }).size === 'small' ? fallback : style;
}
